import { draftStorageKey } from './guided'
import { RENTAL_PATHS, type RentalPath } from './rental'

/**
 * Where a guided draft was being configured: which door, which host, which
 * offer and which tenant maximum.
 *
 * The guided draft (`ddws.console.draft.<wallet>`) records the machine but not
 * the journey, because the journey lives in the Configure URL. Without this a
 * front-door "Continue draft" could only guess the door, and resuming a
 * host-bound draft on the competitive path (or the reverse) changes what the
 * tenant is buying. So a draft with no readable origin gets no resume offer at
 * all — never a guessed one.
 *
 * The origin is read back from `localStorage`, which a tenant can edit, so
 * every field is validated the way a URL parameter would be, and a field that
 * fails is dropped rather than repaired.
 */
export type DraftOrigin = { path?: RentalPath | undefined; host?: string | undefined; offer?: string | undefined; max?: string | undefined }

const HOST_PATTERN = /^[a-z0-9]{8,90}$/
const OFFER_PATTERN = /^[A-Za-z0-9._:-]{1,128}$/
const MAX_PATTERN = /^\d+(\.\d{1,6})?$/

export function draftOriginStorageKey(wallet: string | undefined): string {
  return `${draftStorageKey(wallet)}.origin`
}

function isRentalPath(value: unknown): value is RentalPath {
  return typeof value === 'string' && (RENTAL_PATHS as readonly string[]).includes(value)
}

function valid(value: unknown, pattern: RegExp): value is string {
  return typeof value === 'string' && pattern.test(value)
}

/** JSON with only the known keys, and only valid string values. */
export function serializeDraftOrigin(origin: DraftOrigin): string {
  const out: DraftOrigin = {}
  if (isRentalPath(origin.path)) out.path = origin.path
  if (valid(origin.host, HOST_PATTERN)) out.host = origin.host
  if (valid(origin.offer, OFFER_PATTERN)) out.offer = origin.offer
  if (valid(origin.max, MAX_PATTERN)) out.max = origin.max
  return JSON.stringify(out)
}

/**
 * The stored origin, or `null` when it is missing, unreadable, or names no
 * valid path. An invalid host, offer or max is dropped and the rest kept.
 */
export function parseDraftOrigin(raw: string | null): DraftOrigin | null {
  if (raw === null) return null
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return null
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null
  const record = parsed as Record<string, unknown>
  if (!isRentalPath(record.path)) return null
  const origin: DraftOrigin = { path: record.path }
  if (valid(record.host, HOST_PATTERN)) origin.host = record.host
  if (valid(record.offer, OFFER_PATTERN)) origin.offer = record.offer
  if (valid(record.max, MAX_PATTERN)) origin.max = record.max
  return origin
}

/** The Configure link that reopens the draft where it was left. */
export function resumePathFor(origin: DraftOrigin): string {
  const params = new URLSearchParams()
  if (origin.path) params.set('path', origin.path)
  if (origin.host) params.set('host', origin.host)
  if (origin.offer) params.set('offer', origin.offer)
  if (origin.max) params.set('max', origin.max)
  const query = params.toString()
  return query ? `/app/rent/configure?${query}` : '/app/rent/configure'
}

export function writeDraftOrigin(wallet: string | undefined, origin: DraftOrigin): void {
  try {
    window.localStorage.setItem(draftOriginStorageKey(wallet), serializeDraftOrigin(origin))
  } catch {
    // Storage unavailable: the front door simply offers no resume.
  }
}

export function readDraftOrigin(wallet: string | undefined): DraftOrigin | null {
  try {
    return parseDraftOrigin(window.localStorage.getItem(draftOriginStorageKey(wallet)))
  } catch {
    return null
  }
}

/** Forget the guided draft and its origin. The quote draft is not touched. */
export function discardStoredDraft(wallet: string | undefined): void {
  try {
    window.localStorage.removeItem(draftStorageKey(wallet))
    window.localStorage.removeItem(draftOriginStorageKey(wallet))
  } catch {
    // Nothing to remove when storage is unavailable.
  }
}
